import { ErrorMessage } from "@hookform/error-message";
import { AxiosError } from "axios";
import clsx from "clsx";
import { isEqual } from "lodash-es";
import { FC } from "react";
import { FormProvider, SubmitHandler, useFieldArray, useForm } from "react-hook-form";

import { RubricType, SectionScoreDisplayFormat } from "components/server-types";
import { t } from "i18n/i18n";
import { Listbox } from "mds/components/Listbox";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { CircleInfoIcon, ScoreIcon } from "mds/icons";
import { storeApi, useAppSelector } from "store/index";
import { selectCurrentOrg } from "store/selectors";
import { toastLocalizedOperationError, toastSuccessMessage } from "utils/alerts";
import { BUTTON_EVENT_CATEGORY, trackEvent } from "utils/amplitude";

type FormValues = {
  rubric: RubricType;
  section_score_display_format: SectionScoreDisplayFormat;
};

const getSectionScoreDisplayFormatDropdownValues = () => {
  return Object.values(SectionScoreDisplayFormat).map((value) => ({
    id: value,
    title: t(`home.settings.section_score_display.options.${value}`),
  }));
};

const toastUnknownError = () => {
  toastLocalizedOperationError("failed_to_update_rubric");
};

export const OrgRubricSettings: FC<{ readOnly?: boolean }> = ({ readOnly = false }) => {
  const currentOrg = useAppSelector(selectCurrentOrg);

  const form = useForm<FormValues>({
    defaultValues: {
      rubric: currentOrg?.rubric,
      section_score_display_format:
        currentOrg?.section_score_display_format || SectionScoreDisplayFormat.Percent,
    },
  });

  const { fields: rubricFields } = useFieldArray({
    control: form.control,
    name: "rubric",
  });

  if (!currentOrg) {
    return null;
  }

  const onSubmit: SubmitHandler<FormValues> = (formData) => {
    if (!isEqual(formData.rubric, currentOrg.rubric)) {
      storeApi.orgs
        .partial_update({
          id: currentOrg.id,
          rubric: formData.rubric,
        })
        .then(() => {
          toastSuccessMessage(t("success.toasts.saved_rubric"));
          trackEvent("Org Settings - Save New Rubric", BUTTON_EVENT_CATEGORY);
        })
        .catch((error: unknown) => {
          if (!(error instanceof AxiosError)) {
            toastUnknownError();
            return;
          }
          if (error.response?.status !== 400) {
            toastUnknownError();
            return;
          }
          const respData = error.response?.data as {
            input: string;
            loc: [] | ["levels", number, "name" | "score" | "conversion_percent"];
            msg: string;
            type: string;
            url?: string;
          }[];
          if (!respData) {
            toastUnknownError();
            return;
          }
          respData.forEach(({ loc, msg }) => {
            if (loc.length === 0) {
              form.setError("root", {
                type: "server",
                message: msg,
              });
            } else if (loc[0] === "levels") {
              form.setError(`rubric.${loc[1]}.${loc[2]}`, {
                type: "server",
                message: msg,
              });
            }
          });
        });
    }
    if (formData.section_score_display_format !== currentOrg.section_score_display_format) {
      storeApi.orgs
        .partial_update({
          id: currentOrg.id,
          section_score_display_format: formData.section_score_display_format,
        })
        .then(() => {
          toastSuccessMessage(t("success.toasts.updated_section_score_display_format"));
          trackEvent("Org Settings - Save New Course Score Display Format", BUTTON_EVENT_CATEGORY);
        })
        .catch(() => {
          toastUnknownError();
        });
    }
  };

  const sectionScoreDisplayFormat = form.watch("section_score_display_format");

  return (
    <FormProvider {...form}>
      <form
        className="shadow-level-1 mt-2 bg-black-tint-99 px-3 py-4"
        onSubmit={!readOnly ? form.handleSubmit(onSubmit) : undefined}
      >
        <div className="h6 text-black-tint-40">
          {t("home.settings.rubric_conversion_scale.title")}
        </div>
        <div className="body-s ml-2 mt-2 text-black-tint-40">
          {t("home.settings.rubric_conversion_scale.description")}
        </div>
        <div className="body-s mt-4 flex flex-row items-center gap-2 text-black-tint-20">
          <CircleInfoIcon className="text-blue" />
          <span>{t("home.settings.rubric_conversion_scale.change_alert")}</span>
        </div>

        <div className="my-4 flex flex-col gap-4">
          <ErrorMessage name="root" />
          {rubricFields.map((field, index) => {
            // The first and last levels of the rubric have immutable conversion_percent values of 0% and 100%,
            // respectively. Interior levels have a conversion_percent value that may be edited.
            const disabled = [0, rubricFields.length - 1].includes(index);
            return (
              <div className="flex flex-row gap-2" key={field.id}>
                <ScoreIcon className="mt-0.5" value={field.score} />
                <div className="flex flex-col items-start gap-2">
                  <div className="h4 mb-1 text-black-tint-20">{field.name}</div>
                  <div
                    className={clsx(
                      "mds-text-field-icon",
                      disabled && "cursor-not-allowed bg-black-tint-90 text-black-tint-55",
                    )}
                  >
                    <input
                      key={field.id}
                      {...form.register(`rubric.${index}.conversion_percent`)}
                      className={clsx(
                        "mds-text-field mds-text-field-number text-black-tint-20",
                        disabled && "cursor-not-allowed",
                      )}
                      disabled={disabled || readOnly}
                    />
                    <span>%</span>
                  </div>
                  <ErrorMessage name={`rubric.${index}.conversion_percent`} />
                </div>
              </div>
            );
          })}
        </div>

        <div className="h6 mt-4 text-black-tint-40">
          {t("home.settings.section_score_display.title")}
        </div>
        <div className="body-s ml-2 mt-2 text-black-tint-40">
          {t("home.settings.section_score_display.description")}
        </div>

        <Listbox
          className="ml-2"
          disabled={readOnly}
          eventName="Org Settings - Section score display format selection"
          items={getSectionScoreDisplayFormatDropdownValues()}
          selectedValue={sectionScoreDisplayFormat}
          onSelectionChange={({ id }) => form.setValue("section_score_display_format", id)}
        />

        <SquareButton className="mt-4" disabled={readOnly} kind="accent" size="m" type="submit">
          {t("common.save")}
        </SquareButton>
      </form>
    </FormProvider>
  );
};
